import React, { useEffect, useRef, useState, useMemo } from "react";
import { View, Text, TouchableOpacity } from "react-native";

declare global {
  interface Window {
    Twitch: any;
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YTPlayer {
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  playVideo: () => void;
  pauseVideo: () => void;
  destroy: () => void;
}

interface LiveStreamProps {
  platform: string;
  streamId: string | null;
  width: number | string;
  height: number | string;
  style?: any;
  hideControls?: boolean;
  preventRedirect?: boolean;
}

const LiveStream: React.FC<LiveStreamProps> = ({
  platform,
  streamId,
  width,
  height,
  style,
  hideControls = false,
  preventRedirect = false,
}) => {
  const twitchContainerRef = useRef<HTMLDivElement>(null);
  const youtubePlayerRef = useRef<YTPlayer | null>(null);
  const youtubeContainerRef = useRef<HTMLDivElement>(null);
  const playerIdRef = useRef(
    `twitch-player-${Math.random().toString(36).substring(2, 11)}`
  );
  const youtubePlayerIdRef = useRef(
    `youtube-player-${Math.random().toString(36).substring(2, 11)}`
  );
  const isYoutubePlayerInitialized = useRef(false);
  const [isMuted, setIsMuted] = useState(true);

  // Process YouTube / Twitch IDs
  const processedStreamId = useMemo(() => {
    if (!streamId) return null;

    if (platform?.toLowerCase() === "youtube") {
      if (streamId.includes("v="))
        return streamId.split("v=")[1]?.split("&")[0];
      if (streamId.includes("youtube/"))
        return streamId.split("youtube/")[1]?.split("?")[0];
      if (streamId.includes("embed/"))
        return streamId.split("embed/")[1]?.split("?")[0];
      if (streamId.length === 11 && !/[\s\/]/.test(streamId)) return streamId;
      return streamId;
    }

    if (platform?.toLowerCase() === "twitch") {
      if (streamId.includes("twitch.tv/")) {
        return streamId.split("twitch.tv/")[1]?.split("?")[0]?.split("/")[0];
      }
      return streamId.startsWith("/") ? streamId.substring(1) : streamId;
    }

    return streamId;
  }, [streamId, platform]);

  if (!streamId || streamId.trim() === "" || !processedStreamId) {
    return (
      <View
        style={{
          justifyContent: "center",
          alignItems: "center",
          width,
          height,
          backgroundColor: "#333",
          borderRadius: 8,
          ...style,
        }}
      >
        <Text style={{ color: "#fff" }}>No Stream Found</Text>
      </View>
    );
  }

  // YouTube IFrame API loader
  useEffect(() => {
    if (
      platform === "youtube" &&
      typeof window !== "undefined" &&
      !isYoutubePlayerInitialized.current
    ) {
      const initYouTubePlayer = () => {
        if (
          !window.YT ||
          !window.YT.Player ||
          !processedStreamId ||
          !youtubeContainerRef.current ||
          isYoutubePlayerInitialized.current
        ) {
          return;
        }

        try {
          youtubePlayerRef.current = new window.YT.Player(
            youtubePlayerIdRef.current,
            {
              videoId: processedStreamId,
              width: "100%",
              height: "100%",
              playerVars: {
                autoplay: 1,
                mute: 1,
                playsinline: 1,
                controls: hideControls ? 0 : 1,
                rel: 0,
                modestbranding: 1,
              },
              events: {
                onReady: (event: any) => {
                  event.target.playVideo();
                  setIsMuted(event.target.isMuted());
                },
              },
            }
          );
          isYoutubePlayerInitialized.current = true;
        } catch (error) {
          console.error("Error initializing YouTube player:", error);
        }
      };

      if (!window.YT || !window.YT.Player) {
        // API not loaded yet
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          // Script not added yet, add it
          const tag = document.createElement("script");
          tag.src = "https://www.youtube.com/iframe_api";
          const firstScriptTag = document.getElementsByTagName("script")[0];
          firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);
        }

        // Store existing callback if any
        const existingCallback = window.onYouTubeIframeAPIReady;

        window.onYouTubeIframeAPIReady = () => {
          // Call existing callback first
          if (existingCallback && typeof existingCallback === "function") {
            existingCallback();
          }
          // Then initialize this player
          initYouTubePlayer();
        };
      } else {
        // API already loaded, initialize immediately
        initYouTubePlayer();
      }
    }

    return () => {
      if (youtubePlayerRef.current) {
        try {
          youtubePlayerRef.current.destroy();
          isYoutubePlayerInitialized.current = false;
        } catch (error) {
          console.error("Error destroying YouTube player:", error);
        }
      }
    };
  }, [platform, processedStreamId, hideControls]);

  // Twitch player initialization
  const twitchPlayerRef = useRef<any>(null);

  useEffect(() => {
    if (platform === "twitch" && streamId && typeof window !== "undefined") {
      if (!window.Twitch) {
        const script = document.createElement("script");
        script.src = "https://embed.twitch.tv/embed/v1.js";
        script.onload = () => initTwitch();
        document.head.appendChild(script);
      } else {
        initTwitch();
      }
    }

    function initTwitch() {
      if (!twitchContainerRef.current) return;

      let channel = streamId;
      if (streamId.includes("twitch.tv/")) {
        const parts = streamId.split("twitch.tv/")[1]?.split("/");
        channel = parts?.[0] || streamId;
      }

      console.log("Twitch player init:", { streamId, channel });

      try {
        // Clear any existing player
        twitchContainerRef.current.innerHTML = "";

        // Use Twitch.Embed for better autoplay support
        const embed = new window.Twitch.Embed(twitchContainerRef.current, {
          width: "100%",
          height: "100%",
          channel: channel,
          autoplay: true,
          muted: true, // Must be muted for autoplay to work
          layout: "video", // Only show video, no chat
          allowfullscreen: true,
          parent: [window.location.hostname, "localhost"],
        });

        // Store embed reference
        twitchPlayerRef.current = embed;

        // Listen for READY event to ensure player is fully loaded
        embed.addEventListener(window.Twitch.Embed.VIDEO_READY, () => {
          const player = embed.getPlayer();
          if (player) {
            // Ensure the video plays
            player.play();
            player.setMuted(true); // Ensure muted for autoplay policy
            console.log("Twitch player VIDEO_READY - started playing");
          }
        });

        // Listen for PLAY event
        embed.addEventListener(window.Twitch.Embed.VIDEO_PLAY, () => {
          console.log("Twitch player VIDEO_PLAY event fired");
        });

      } catch (error) {
        console.error("Error initializing Twitch player:", error);
      }
    }

    // Cleanup function
    return () => {
      if (twitchPlayerRef.current) {
        try {
          twitchPlayerRef.current = null;
        } catch (error) {
          console.error("Error cleaning up Twitch player:", error);
        }
      }
    };
  }, [platform, streamId, hideControls]);

  return (
    <View
      style={{
        width,
        height,
        position: "relative",
        borderRadius: 8,
        overflow: "hidden",
        ...style,
      }}
    >
      {platform === "youtube" && (
        <View style={{ width: "100%", height: "100%", position: "relative" }}>
          <div
            id={youtubePlayerIdRef.current}
            ref={youtubeContainerRef}
            style={{ width: "100%", height: "100%", borderRadius: 8 }}
          />
          <TouchableOpacity
            onPress={() => {
              if (youtubePlayerRef.current) {
                if (youtubePlayerRef.current.isMuted()) {
                  youtubePlayerRef.current.unMute();
                  setIsMuted(false);
                } else {
                  youtubePlayerRef.current.mute();
                  setIsMuted(true);
                }
              }
            }}
            style={{
              position: "absolute",
              bottom: 10,
              left: 10,
              backgroundColor: "rgba(0,0,0,0.6)",
              borderRadius: 6,
              padding: 8,
              zIndex: 100,
            }}
          >
            <Text style={{ color: "white", fontSize: 16 }}>
              {isMuted ? "🔇" : "🔊"}
            </Text>
          </TouchableOpacity>

          {/* Top overlay - full width to prevent clicking title */}
          <View
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: 60,
              backgroundColor: "transparent",
              zIndex: 10,
            }}
            pointerEvents="auto"
          />
          {/* Bottom overlay - 60% from right to prevent clicking YouTube logo and controls */}
          <View
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "60%",
              height: 40,
              backgroundColor: "transparent",
              zIndex: 10,
            }}
            pointerEvents="auto"
          />
        </View>
      )}

      {platform === "twitch" && (
        <View style={{ width: "100%", height: "100%", position: "relative" }}>
          <div
            id={playerIdRef.current}
            ref={twitchContainerRef}
            style={{ width: "100%", height: "100%" }}
          />
          {preventRedirect && (
            <>
              {/* Top overlay - full width to prevent clicking title */}
              <View
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "15%",
                  backgroundColor: "transparent",
                  zIndex: 10,
                }}
                pointerEvents="auto"
              />
              {/* Bottom overlay - 60% from right to prevent clicking Twitch logo and controls */}
              <View
                style={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: "60%",
                  height: "15%",
                  backgroundColor: "transparent",
                  zIndex: 10,
                }}
                pointerEvents="auto"
              />
            </>
          )}
        </View>
      )}
    </View>
  );
};

export default LiveStream;
